import { hasAnyPermission, useAuthorization } from '@frontend/authorization';
import { ClassType, EntityType, calculateTimeDifference } from '@frontend/common';
import { DefaultPrimaryButton, DefaultSecondaryButton } from '@frontend/components/common';
import { ConfirmationModal } from '@frontend/components/modal';
import { ErrorHandler } from '@frontend/error-handler';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { ToastUtil } from '@frontend/toast-utils';
import { TransactionClient } from '@frontend/transaction/api';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { Transaction } from '@frontend/transaction/types';
import { useLayoutEffect, useRef, useState } from 'react';
import { LuExternalLink } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import {
    canContinueWithExistingDraftTransaction,
    canDeleteExistingDraftTransaction,
    getTransactionDeletePermissions,
    getTransactionDetailUrl
} from './existing-tracking-number.utils';

interface ExistingTrackingNumberMatchProps {
    transaction: Transaction;
    currentUserId: string | null;
    isContinuing?: boolean;
    onContinueWithTransaction?: (transaction: Transaction) => void;
    onTransactionDeleted?: () => void;
    className?: string;
}

const CARD_HORIZONTAL_PADDING_PX = 32; // p-4 on each side
const GRID_GAP_PX = 8; // gap-2

const ExistingTrackingNumberMatch = ({
    transaction,
    currentUserId,
    isContinuing = false,
    onContinueWithTransaction,
    onTransactionDeleted,
    className
}: ExistingTrackingNumberMatchProps) => {
    const { openModal, closeTopModal } = useModals();
    const { permissions } = useAuthorization();
    const canContinue = canContinueWithExistingDraftTransaction(transaction, currentUserId);
    const canDelete =
        canDeleteExistingDraftTransaction(transaction, currentUserId) &&
        !!permissions &&
        hasAnyPermission(permissions, getTransactionDeletePermissions(transaction));
    const cardRef = useRef<HTMLDivElement>(null);
    const buttonsRef = useRef<HTMLDivElement>(null);
    const [spanFullWidth, setSpanFullWidth] = useState(false);
    const deleteModalId = `delete-existing-tracking-number-transaction-${transaction.id}`;

    useLayoutEffect(() => {
        const card = cardRef.current;
        const buttons = buttonsRef.current;
        const parent = card?.parentElement;
        if (!card || !buttons || !parent) return;

        const updateWidth = () => {
            const halfWidth = (parent.clientWidth - GRID_GAP_PX) / 2;
            const availableForButtons = halfWidth - CARD_HORIZONTAL_PADDING_PX;
            setSpanFullWidth(buttons.scrollWidth > availableForButtons);
        };

        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(parent);
        return () => observer.disconnect();
    }, [canContinue, canDelete, isContinuing]);

    const openDeleteModal = () => {
        const displayName = transaction.tracking_number || transaction.id;
        openModal(deleteModalId, () => (
            <ConfirmationModal
                show={true}
                handleClose={() => closeTopModal()}
                onConfirm={() => {
                    TransactionClient.deleteTransaction(transaction.account_id, transaction.id)
                        .then(() => {
                            closeTopModal();
                            ToastUtil.success(CommonMessage.TOAST.DELETE.SUCCESS(CommonMessage.OBJECTS.TRANSACTION.DEFAULT));
                            onTransactionDeleted?.();
                        })
                        .catch(ErrorHandler.handleError);
                }}
                message={CommonMessage.FORMS.DELETE_OBJECT(CommonMessage.OBJECTS.TRANSACTION.DEFAULT, displayName)}
                severity={ClassType.DANGER}
                cascadeWarning
            />
        ));
    };

    return (
        <div
            ref={cardRef}
            className={`outline-border-default inline-flex w-full flex-col items-start justify-start gap-2 self-stretch rounded-xl p-4 outline-1 ${spanFullWidth ? 'col-span-2' : 'col-span-1'} ${className ?? ''}`}>
            <div className='inline-flex min-h-6 items-center justify-between gap-4 self-stretch'>
                <div className='text-brand-muted shrink-0 justify-start text-base font-medium'>{CommonMessage.OBJECTS.COMMON_FIELDS.STATUS}</div>
                <TransactionStateBadge state={transaction.state} />
            </div>
            <div className='inline-flex min-h-6 items-center justify-between gap-4 self-stretch'>
                <div className='text-brand-muted shrink-0 justify-start text-base font-medium'>
                    <FormattedMessage
                        id='Creator'
                        description='Creator of a transaction (based on user_id)'
                        defaultMessage='Creator'
                    />
                </div>
                {transaction.user_id ? (
                    <PlatformObjectBadge
                        className='text-foreground-default flex items-center justify-end gap-1 text-base font-medium'
                        entityType={EntityType.USER}
                        id={transaction.user_id}
                        disableClick
                    />
                ) : (
                    <div className='text-foreground-default justify-start text-base font-medium'>/</div>
                )}
            </div>
            <div className='inline-flex min-h-6 items-center justify-between gap-4 self-stretch'>
                <div className='text-brand-muted shrink-0 justify-start text-base font-medium'>{CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT}</div>
                <div className='text-foreground-default justify-start text-base font-medium'>{calculateTimeDifference(transaction.creation_timestamp)}</div>
            </div>
            <div
                ref={buttonsRef}
                className='flex w-max max-w-none flex-nowrap justify-end gap-2 self-end pt-2'>
                <DefaultSecondaryButton
                    text={CommonMessage.DETAIL.VIEW_DETAILS}
                    icon={<LuExternalLink className='size-4 shrink-0' />}
                    href={getTransactionDetailUrl(transaction)}
                    target='_blank'
                    rel='noopener noreferrer'
                    size='sm'
                />
                {canDelete && (
                    <DefaultSecondaryButton
                        text={CommonMessage.BUTTONS.DELETE}
                        onClick={openDeleteModal}
                        size='sm'
                    />
                )}
                {canContinue && (
                    <DefaultPrimaryButton
                        text={CommonMessage.BUTTONS.CONTINUE}
                        onClick={() => onContinueWithTransaction?.(transaction)}
                        disabled={isContinuing}
                        size='sm'
                    />
                )}
            </div>
        </div>
    );
};

export default ExistingTrackingNumberMatch;
